import React, { useEffect, useState, useRef, useLayoutEffect } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useConfigId } from '../contexts/ConfigContext'
import { getGraphSettings, getPrompts } from '../api/graph_settings'
import { NODES, AGENT_CONVERSATION_MESSAGES_KEYS, PROACTIVE_SETTING_KEYS } from '../config/graphEntities'

/**
 * Единственная переменная масштаба. Меняй — вся схема масштабируется пропорционально.
 * 1 = 100%, 0.7 = 70%, 1.2 = 120%. Все размеры (узлы, шрифты, стрелки, отступы) зависят от неё.
 */
const SCALE = 1.3

const GRAPH_COLORS = {
  dark: {
    agent: { fill: '#7c9cff', stroke: '#6b8aef', text: '#0f0f14', hover: '#9cacff' },
    rule: { fill: '#3a3a4a', stroke: '#4a4a5a', text: '#e8eaef', hover: '#4a4a5a' },
    io: { fill: '#252530', stroke: '#3a3a4a', text: '#e8eaef', hover: '#3a3a4a' },
  },
  light: {
    agent: { fill: '#3b6cff', stroke: '#2f5df0', text: '#ffffff', hover: '#5b8aff' },
    rule: { fill: '#c8cdde', stroke: '#a8b0c8', text: '#131626', hover: '#d6dae6' },
    io: { fill: '#e5e7ef', stroke: '#d6dae6', text: '#131626', hover: '#e8ecf4' },
  },
}

const NODE_ROUTE_KEYS = {
  IN: ['nodes', 'in'],
  MA: ['agents', 'message_analyzer'],
  MS: ['agents', 'memory_summary'],
  SG: ['agents', 'message_generator'],
  PS: ['agents', 'policy_safety'],
  OG: ['agents', 'offer_generator'],
  CB: ['nodes', 'core'],
  MEDIA: ['nodes', 'media_selector'],
  SEND: ['nodes', 'send'],
  PROACTIVE: ['proactive'],
  PMG: ['agents', 'proactive_message_generator'],
}

/**
 * Layout: левая часть — основной поток, правая — проактивный, отправка внизу по центру.
 */
const LAYOUT = {
  /* Левая часть — основной поток */
  IN: { x: 120, y: 35, w: 90, h: 28, type: 'io' },
  MA: { x: 30, y: 95, w: 120, h: 30, type: 'agent' },
  MS: { x: 210, y: 95, w: 120, h: 30, type: 'agent' },
  CB: { x: 120, y: 155, w: 80, h: 30, type: 'rule' },
  MEDIA: { x: 120, y: 215, w: 120, h: 30, type: 'rule' },
  OG: { x: 30, y: 275, w: 120, h: 30, type: 'agent' },
  SG: { x: 210, y: 275, w: 120, h: 30, type: 'agent' },
  PS: { x: 120, y: 335, w: 120, h: 30, type: 'agent' },
  /* Правая часть — проактивный поток */
  PROACTIVE: { x: 420, y: 95, w: 110, h: 28, type: 'io' },
  PMG: { x: 420, y: 155, w: 130, h: 30, type: 'agent' },
  /* Внизу по центру */
  SEND: { x: 250, y: 400, w: 90, h: 28, type: 'io' },
}

/** Рёбра: from -> to */
const EDGES = [
  ['IN', 'MA'], ['IN', 'MS'],
  ['MA', 'CB'], ['MS', 'CB'],
  ['CB', 'MEDIA'],
  ['MEDIA', 'OG'], ['MEDIA', 'SG'],
  ['OG', 'PS'], ['SG', 'PS'],
  ['PS', 'SEND'],
  ['PROACTIVE', 'PMG'], ['PMG', 'SEND'],
]

/** Границы layout — считаются из LAYOUT, viewBox зависит от них */
const LAYOUT_BOUNDS = (() => {
  let maxX = 0, maxY = 0
  for (const l of Object.values(LAYOUT)) {
    maxX = Math.max(maxX, l.x + l.w / 2)
    maxY = Math.max(maxY, l.y + l.h / 2)
  }
  return { maxX, maxY }
})()
const PADDING_BASE = 24
/** Базовая ширина блока отображения при SCALE=1 (px). Схема центрируется внутри. */
const BASE_DISPLAY_PX = 600

function buildBasePath(configId, configRoot = '/settings/configuration') {
  return configId ? `${configRoot}/${configId}` : ''
}

function buildNodeHref(configId, nodeId, configRoot) {
  const parts = NODE_ROUTE_KEYS[nodeId]
  if (!parts || !configId) return '#'
  const base = buildBasePath(configId, configRoot)
  return parts.length === 1 ? `${base}/${parts[0]}` : `${base}/${parts[0]}/${parts[1]}`
}

function computeNodeStats(settings = {}, agents = {}, promptsData = []) {
  const stats = {}
  const isEmpty = (v) => v == null || v === ''
  const promptsByAgent = {}
  for (const p of promptsData || []) {
    if (p.role === 'system') promptsByAgent[p.agent_key] = isEmpty(p.base) ? 1 : 0
  }
  for (const [nodeId, parts] of Object.entries(NODE_ROUTE_KEYS)) {
    let total = 0
    let unfilled = 0
    if (parts[0] === 'nodes') {
      const node = NODES.find((n) => n.key === parts[1])
      const keys = node?.settingKeys || []
      total = keys.length
      unfilled = keys.filter((k) => isEmpty(settings[k])).length
    } else if (parts[0] === 'agents') {
      total = 2
      const agentKey = parts[1]
      const agent = agents[agentKey] || {}
      if (isEmpty(agent.openai_model)) unfilled += 1
      if (promptsByAgent[agentKey]) unfilled += 1
      const convKey = AGENT_CONVERSATION_MESSAGES_KEYS[agentKey]
      if (convKey) {
        total += 1
        if (isEmpty(settings[convKey])) unfilled += 1
      }
    } else if (parts[0] === 'proactive') {
      total = PROACTIVE_SETTING_KEYS.length
      unfilled = PROACTIVE_SETTING_KEYS.filter((k) => isEmpty(settings[k])).length
    }
    if (total > 0) stats[nodeId] = { filled: total - unfilled, total }
  }
  return stats
}

/** Путь стрелки от центра одного узла к центру другого */
function edgePath(from, to, scale) {
  const fx = from.x * scale
  const fy = from.y * scale
  const tx = to.x * scale
  const ty = to.y * scale
  const dy = ty - fy
  const dx = tx - fx
  const midY = fy + dy * 0.5
  return `M ${fx} ${fy} C ${fx} ${midY}, ${tx} ${midY}, ${tx} ${ty}`
}

function FallbackNode({ to, children, agent }) {
  return (
    <Link
      to={to}
      className={`graph-fallback-node ${agent ? 'graph-fallback-agent' : ''} graph-node-clickable`}
    >
      {children}
    </Link>
  )
}

function GraphDiagramFallback({ t, configId, configRoot }) {
  const base = buildBasePath(configId, configRoot)
  const agents = [
    { label: t('diagram.messageAnalyzer'), to: `${base}/agents/message_analyzer` },
    { label: t('diagram.memorySummarizer'), to: `${base}/agents/memory_summary` },
    { label: t('diagram.messageGenerator'), to: `${base}/agents/message_generator` },
    { label: t('diagram.offerGenerator'), to: `${base}/agents/offer_generator` },
    { label: t('diagram.contentModerator'), to: `${base}/agents/policy_safety` },
  ]
  if (!configId) return null
  return (
    <div className="graph-fallback">
      <div className="graph-fallback-section">
        <div className="graph-fallback-label">🤖 {t('diagram.llmAgents')}</div>
        <div className="graph-fallback-agents">
          {agents.map((a) => (
            <FallbackNode key={a.to} to={a.to} agent>
              {a.label}
            </FallbackNode>
          ))}
        </div>
      </div>
      <div className="graph-fallback-flow">
        <FallbackNode to={`${base}/nodes/in`}>{t('diagram.incomingMessage')}</FallbackNode>
        <span className="graph-fallback-arrow">↓</span>
        <div className="graph-fallback-branch graph-fallback-parallel">
          <div className="graph-fallback-path">
            <FallbackNode to={`${base}/agents/message_analyzer`} agent>{t('diagram.messageAnalyzer')}</FallbackNode>
          </div>
          <div className="graph-fallback-parallel-label">∥ {t('diagram.parallel')}</div>
          <div className="graph-fallback-path">
            <FallbackNode to={`${base}/agents/memory_summary`} agent>{t('diagram.memorySummarizerEvery10')}</FallbackNode>
          </div>
        </div>
        <span className="graph-fallback-arrow">↓</span>
        <FallbackNode to={`${base}/nodes/core`}>{t('diagram.core')}</FallbackNode>
        <span className="graph-fallback-arrow">↓</span>
        <FallbackNode to={`${base}/nodes/media_selector`}>{t('diagram.mediaSelector')}</FallbackNode>
        <span className="graph-fallback-arrow">↓</span>
        <div className="graph-fallback-branch">
          <div className="graph-fallback-path">
            <FallbackNode to={`${base}/agents/offer_generator`} agent>{t('diagram.offerGenerator')}</FallbackNode>
            <span className="graph-fallback-arrow">↓</span>
            <FallbackNode to={`${base}/agents/policy_safety`} agent>{t('diagram.contentModerator')}</FallbackNode>
            <span className="graph-fallback-arrow">↓</span>
            <FallbackNode to={`${base}/nodes/send`}>{t('diagram.sendSale')}</FallbackNode>
          </div>
          <div className="graph-fallback-path">
            <FallbackNode to={`${base}/agents/message_generator`} agent>{t('diagram.messageGenerator')}</FallbackNode>
            <span className="graph-fallback-arrow">↓</span>
            <FallbackNode to={`${base}/agents/policy_safety`} agent>{t('diagram.contentModerator')}</FallbackNode>
            <span className="graph-fallback-arrow">↓</span>
            <FallbackNode to={`${base}/nodes/send`}>{t('diagram.sendChat')}</FallbackNode>
          </div>
        </div>
      </div>
      <div className="graph-fallback-note">
        {t('diagram.parallelNote')}
      </div>
      <div className="graph-fallback-section graph-fallback-proactive">
        <div className="graph-fallback-label">⏰ {t('diagram.proactiveFlow')}</div>
        <div className="graph-fallback-flow">
          <FallbackNode to={`${base}/proactive`}>{t('diagram.proactiveBot')}</FallbackNode>
          <span className="graph-fallback-arrow">→</span>
          <FallbackNode to={`${base}/agents/proactive_message_generator`} agent>{t('diagram.proactiveGenerator')}</FallbackNode>
          <span className="graph-fallback-arrow">→</span>
          <FallbackNode to={`${base}/nodes/send`}>{t('diagram.send')}</FallbackNode>
        </div>
      </div>
    </div>
  )
}

const LABEL_KEYS = {
  IN: 'in',
  MA: 'messageAnalyzer',
  MS: 'memorySummarizer',
  SG: 'messageGenerator',
  PS: 'contentModerator',
  OG: 'offerGenerator',
  CB: 'core',
  MEDIA: 'mediaSelector',
  SEND: 'send',
  PROACTIVE: 'proactiveBot',
  PMG: 'proactiveGenerator',
}

const TEXT_PADDING_X = 12
const TEXT_PADDING_Y = 8

export default function GraphDiagram() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const configId = useConfigId()
  const configRoot = pathname.startsWith('/v2') ? '/v2/configuration' : '/old/settings/configuration'
  const [nodeStats, setNodeStats] = useState({})
  const [hovered, setHovered] = useState(null)
  const [nodeSizes, setNodeSizes] = useState({})
  const textRefs = useRef({})
  const svgRef = useRef(null)

  const isDark = typeof document !== 'undefined' && document.documentElement?.dataset?.theme !== 'light'
  const colors = GRAPH_COLORS[isDark ? 'dark' : 'light']
  const s = SCALE

  useLayoutEffect(() => {
    const sizes = {}
    for (const [nodeId, el] of Object.entries(textRefs.current)) {
      if (el && svgRef.current) {
        const svg = svgRef.current
        const bbox = el.getBBox()
        const minW = LAYOUT[nodeId].w * s
        const minH = LAYOUT[nodeId].h * s
        sizes[nodeId] = {
          w: Math.max(minW, bbox.width + TEXT_PADDING_X * s),
          h: Math.max(minH, bbox.height + TEXT_PADDING_Y * s),
        }
      }
    }
    setNodeSizes((prev) => {
      const next = { ...prev, ...sizes }
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    })
  }, [s, t])

  useEffect(() => {
    if (!configId) return
    Promise.all([getGraphSettings(configId), getPrompts(configId)])
      .then(([gs, promptsRes]) => {
        setNodeStats(computeNodeStats(
          gs?.settings || {},
          gs?.agents || {},
          promptsRes?.data || []
        ))
      })
      .catch(() => setNodeStats({}))
  }, [configId])

  const handleNodeClick = (nodeId) => {
    const href = buildNodeHref(configId, nodeId, configRoot)
    if (href && href !== '#') navigate(href)
  }

  const padding = PADDING_BASE * s
  const bounds = (() => {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
    for (const [nodeId, layout] of Object.entries(LAYOUT)) {
      const sz = nodeSizes[nodeId]
      const w = sz ? sz.w : layout.w * s
      const h = sz ? sz.h : layout.h * s
      const cx = layout.x * s
      const cy = layout.y * s
      minX = Math.min(minX, cx - w / 2)
      maxX = Math.max(maxX, cx + w / 2)
      minY = Math.min(minY, cy - h / 2)
      maxY = Math.max(maxY, cy + h / 2)
    }
    if (!Number.isFinite(minX)) {
      minX = 0
      maxX = LAYOUT_BOUNDS.maxX * s
      minY = 0
      maxY = LAYOUT_BOUNDS.maxY * s
    }
    return { minX, maxX, minY, maxY }
  })()
  const viewX = bounds.minX - padding
  const viewY = bounds.minY - padding
  const viewW = bounds.maxX - bounds.minX + padding * 2
  const viewH = bounds.maxY - bounds.minY + padding * 2

  return (
    <div className={`graph-diagram-wrapper ${isDark ? 'graph-diagram-wrapper--dark' : 'graph-diagram-wrapper--light'}`}>
      <div className="graph-diagram graph-diagram--custom">
        <svg
          ref={svgRef}
          viewBox={`${viewX} ${viewY} ${viewW} ${viewH}`}
          className="graph-diagram-svg"
          style={{ width: BASE_DISPLAY_PX * s, maxWidth: '100%', height: 'auto' }}
        >
          <defs>
            <marker
              id="arrow"
              markerUnits="userSpaceOnUse"
              markerWidth={8 * s}
              markerHeight={8 * s}
              refX={7 * s}
              refY={4 * s}
              orient="auto"
            >
              <path d="M0,0 L8,4 L0,8 Z" fill={colors.io.stroke} />
            </marker>
          </defs>
          {/* Рёбра */}
          <g className="graph-edges">
            {EDGES.map(([fromId, toId]) => {
              const from = LAYOUT[fromId]
              const to = LAYOUT[toId]
              if (!from || !to) return null
              return (
                <path
                  key={`${fromId}-${toId}`}
                  d={edgePath(from, to, s)}
                  fill="none"
                  stroke={colors.io.stroke}
                  strokeWidth={1.5 * s}
                  markerEnd="url(#arrow)"
                />
              )
            })}
          </g>
          {/* Узлы */}
          <g className="graph-nodes">
            {Object.entries(LAYOUT).map(([nodeId, layout]) => {
              const type = layout.type
              const c = colors[type]
              const isHover = hovered === nodeId
              const fill = isHover ? (c.hover || c.fill) : c.fill
              const sz = nodeSizes[nodeId]
              const w = sz ? sz.w : layout.w * s
              const h = sz ? sz.h : layout.h * s
              const stat = nodeStats[nodeId]
              const badgeW = 22 * s
              const badgeH = 14 * s
              const badgeRx = 7 * s

              return (
                <g
                  key={nodeId}
                  className={`graph-node graph-node--${type} ${configId ? 'graph-node--clickable' : ''}`}
                  transform={`translate(${layout.x * s}, ${layout.y * s})`}
                  style={{ cursor: configId ? 'pointer' : 'default' }}
                  onClick={() => configId && handleNodeClick(nodeId)}
                  onMouseEnter={() => setHovered(nodeId)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <rect
                    x={-w / 2}
                    y={-h / 2}
                    width={w}
                    height={h}
                    rx={6 * s}
                    ry={6 * s}
                    fill={fill}
                    stroke={c.stroke}
                    strokeWidth={1.5 * s}
                  />
                  <text
                    ref={(el) => { textRefs.current[nodeId] = el }}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={c.text}
                    fontSize={11 * s}
                    fontWeight={500}
                  >
                    {t(`diagram.${LABEL_KEYS[nodeId]}`)}
                  </text>
                  {stat && stat.total > 0 && (
                    <g transform={`translate(${w / 2 - badgeW / 2}, ${-h / 2 - badgeH / 2})`}>
                      <rect
                        width={badgeW}
                        height={badgeH}
                        rx={badgeRx}
                        ry={badgeRx}
                        fill={stat.filled < stat.total ? '#c33' : '#4a7a4a'}
                        stroke={stat.filled < stat.total ? '#a22' : '#3a6a3a'}
                        strokeWidth={1 * s}
                      />
                      <text
                        x={badgeW / 2}
                        y={badgeH / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#000"
                        fontSize={9 * s}
                        fontWeight={600}
                      >
                        {stat.filled}/{stat.total}
                      </text>
                    </g>
                  )}
                </g>
              )
            })}
          </g>
        </svg>
      </div>
    </div>
  )
}
